import { PageType, RoundednessLevel, ScheduleType } from "@prisma/client";
import * as Sentry from "@sentry/nextjs";
import axios from "axios";
import { clsx } from "clsx";
import QRCode from "qrcode";
import { twMerge } from "tailwind-merge";
import tinycolor from "tinycolor2";
import { validate as isUuid } from "uuid";

import { reboltToast } from "@/components/toasts";
import { installAuthRecoveryInterceptor } from "@/lib/auth/interceptor";
import { isProd } from "@/lib/env";
import { ExpectedError, ExternalError } from "@/lib/errors";
import { ERROR_TYPES } from "../errors/errorTypes";

const EXPECTED_ERROR_TYPE = "ExpectedError";
const NETWORK_ERROR_TYPE = "NetworkError";

// fallback for v1 codes we treat as expected client rejections (toast only, no
// sentry). the primary mechanism is the server's reportToSentry flag, which
// errorResponse now stamps on the wire for these — formatApiError honors it
// below (see reportToSentry). this set stays as a belt-and-suspenders guard for
// responses that predate the flag. not_found and validation_error are
// deliberately omitted so a genuine missing-resource or malformed-request bug
// from our own frontend still surfaces in sentry.
const EXPECTED_API_ERROR_CODES = new Set(["bad_request", "conflict"]);

export const handleError = (error, customMessage = "Error") => {
  console.error(customMessage, error);
  reboltToast.error(`${customMessage}: ${error?.response?.data?.message || error?.message || "Unknown error"}`);
};

export const formatZodError = (error) => {
  return error.issues.map((issue) => `${issue.path.join(".")}: ${issue.message}`).join("; ");
};

export const validateCSS = (css) => {
  if (typeof css !== "string") return false;

  css = css.trim();

  if (!css) return true;

  return true;
};

export const getHeightAndWidth = (ar) => {
  let aspectRatio = ar || 1;
  let height;

  if (aspectRatio >= 2.5) {
    height = 45;
  } else if (aspectRatio >= 1.5 && aspectRatio < 2.5) {
    height = 55;
  } else {
    height = 65;
  }

  const width = height * aspectRatio;

  return { height: Math.round(height), width: Math.round(width) };
};

export function logError(customMessage, error, res, response) {
  //Explicitly log the error to Sentry
  const responseMessage = response?.message || customMessage || "An unknown error occurred";

  const errorToCapture = error instanceof Error ? error : new Error(responseMessage);
  //Explicitly extract the cause of the error if it exists (this provides better context into external errors we are throwing)
  const errorCause = error?.cause || null;

  Sentry.withScope((scope) => {
    if (response?.type) scope.setTag("type", response.type);
    if (res?.req) {
      scope.setContext("requestMeta", {
        method: res.req.method,
        url: res.req.url,
      });
    }
    scope.setExtra("customMessage", customMessage);
    scope.setExtra("error", error);
    scope.setExtra("errorCause", errorCause);
    scope.setExtra("responseMessage", responseMessage);

    // Legacy response objects can carry `type: "ExpectedError"`, so probe the response too.
    const shouldSkip = shouldSkipSentryCapture([errorToCapture, error, response]);

    if (!shouldSkip) {
      Sentry.captureException(errorToCapture);
    }
  });

  if (typeof res !== "undefined" && response) {
    const body = { message: responseMessage, type: response.type };
    // propagate the server's sentry decision so the client can mirror it without
    // re-deriving expectedness per hook. only sent when opting out, to keep the
    // payload lean and the default (report) implicit.
    if (response.reportToSentry === false) {
      body.reportToSentry = false;
    }
    res.status(response.statusCode).json(body);
  }
}

export function logFrontendError(customMessage, error, toastMessage) {
  if (typeof toastMessage !== "undefined") {
    reboltToast.error(toastMessage, { duration: 2000 });
  }

  //Don't report to Sentry if the error is an ExpectedError or NetworkError
  if (shouldSkipSentryCapture([error], { includeNetworkError: true })) {
    return;
  }

  // when the error is a real Error we let sentry group it by its own stack.
  // formatApiError returns a plain object, so without help every api failure
  // would collapse into one issue (the `new Error` stack lives on this line).
  // give it a descriptive name and an explicit fingerprint so distinct
  // endpoints/status codes stay separate issues.
  let errorToCapture;
  let fingerprint;

  if (error instanceof Error) {
    errorToCapture = error;
  } else {
    errorToCapture = new Error(customMessage);
    fingerprint = buildFrontendErrorFingerprint(error);
    if (fingerprint && error?.type) {
      errorToCapture.name = error.type;
    }
  }

  Sentry.captureException(errorToCapture, {
    fingerprint,
    extra: {
      error,
      toastMessage,
      customMessage,
    },
  });
}

// a path segment is treated as an id (and collapsed to :id) when it matches one
// of the id shapes this app emits. uuid uses the `uuid` package's validator for
// precision; the rest are anchored regexes (cuid2's own isCuid() is too loose —
// it matches plain route words like "quotes").
function isIdSegment(segment) {
  if (isUuid(segment)) return true; // 550e8400-e29b-41d4-a716-446655440000
  if (/^c?[a-z0-9]{24}$/.test(segment)) return true; // prisma cuid / cuid2
  if (/^[a-z]+_[A-Za-z0-9]{6,}$/.test(segment)) return true; // stripe cus_… / clerk user_…
  if (/^\d{4,}$/.test(segment)) return true; // long numeric ids
  return false;
}

// collapse dynamic id segments to a placeholder so the same endpoint groups
// together regardless of which record it hit. the WHATWG URL parser (with a
// dummy base for relative axios urls) strips query + fragment reliably —
// split("?") misses #fragments and absolute-vs-relative differences.
function templatizeUrl(url) {
  if (typeof url !== "string" || url.length === 0) {
    return "unknown";
  }
  let pathname;
  try {
    pathname = new URL(url, "http://_").pathname;
  } catch {
    pathname = url.split("?")[0];
  }
  return pathname
    .split("/")
    .map((segment) => (segment && isIdSegment(segment) ? ":id" : segment))
    .join("/");
}

// derive a stable sentry fingerprint from a formatApiError result so unrelated
// api failures don't share one issue. returns undefined for objects that aren't
// formatted api errors (let sentry fall back to default grouping).
function buildFrontendErrorFingerprint(error) {
  if (!error || typeof error !== "object") {
    return undefined;
  }

  const { type, status, method, url } = error;
  if (status === undefined && url === undefined && type === undefined) {
    return undefined;
  }

  return [
    "frontend-api-error",
    type || "UnknownError",
    status != null ? String(status) : "none",
    (method || "get").toLowerCase(),
    templatizeUrl(url),
  ];
}

function shouldSkipSentryCapture(errors, options = {}) {
  return errors.some((error) => isSilentSentryError(error, options));
}

function isSilentSentryError(error, { includeNetworkError = false } = {}) {
  if (!error || typeof error !== "object") {
    return false;
  }

  return (
    error.reportToSentry === false ||
    error.name === EXPECTED_ERROR_TYPE ||
    error.type === EXPECTED_ERROR_TYPE ||
    (includeNetworkError && error.type === NETWORK_ERROR_TYPE)
  );
}

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function slugify(str) {
  if (!str || typeof str !== "string") return "";
  str = str.toLowerCase().trim();
  str = str
    .replace(/\s+/g, "-") // replace spaces with dashes
    .replace(/[^a-z0-9-]/g, "") // remove non-alphanumeric chars
    .replace(/-+/g, "-") // replace multiple dashes with single dash
    .replace(/^-+|-+$/g, ""); // remove leading/trailing dashes

  return str;
}

const api = axios.create({
  baseURL: "",
  headers: {
    "Content-Type": "application/json",
    // Any other default headers
  },
});

installAuthRecoveryInterceptor(api);

export const capitalize = (str) => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/** Uppercases only the first character, preserving the rest of the string as-is.
 *  Use instead of `capitalize` when casing matters (e.g. names like "McDonald"). */
export const capitalizeFirst = (str) => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export { api };

export const formatApiError = (error, overrideErrorMessage) => {
  let message = "Unknown error occurred";
  let errorDetails = {
    originalMessage: error?.message,
    status: error?.response?.status,
    url: error?.config?.url,
    method: error?.config?.method,
    type: error?.response?.data?.type || error?.response?.data?.error?.type || "UnknownError",
    // the server stamps reportToSentry:false on errors it deems expected
    // (auth lapses, business-rule rejections). honoring it here means every
    // caller — handleApiRequest, logFrontendError, the global SWR onError —
    // skips sentry automatically, with no per-hook opt-in. (single source of truth)
    reportToSentry: error?.response?.data?.reportToSentry ?? error?.response?.data?.error?.reportToSentry,
  };

  if (error instanceof ExpectedError) {
    message = error.message;
    errorDetails.type = "ExpectedError";
  } else if (error?.response) {
    const apiError = error.response?.data?.error;

    // v1 validation errors carry field-level messages in details.errors —
    // surface those instead of the generic "Request body validation failed"
    const fieldErrors = apiError?.details?.errors;
    const fieldMessage = Array.isArray(fieldErrors) && fieldErrors.length > 0 ? fieldErrors[0].message : null;

    message =
      overrideErrorMessage ||
      fieldMessage ||
      apiError?.message || // v1 API format: { success: false, error: { message: "..." } }
      error.response?.data?.message || // legacy format
      errorDetails.originalMessage ||
      "An error occurred. We're looking into it.";

    // deliberate client rejections from our api are expected — toast only, no sentry.
    if (apiError?.code && EXPECTED_API_ERROR_CODES.has(apiError.code)) {
      errorDetails.type = EXPECTED_ERROR_TYPE;
    }
  } else if (error?.request) {
    message = errorDetails.originalMessage || "No response received from the server";
    errorDetails.type = "NetworkError";
  } else {
    message = errorDetails.originalMessage || "Unknown error";
    errorDetails.type = "UnknownError";
  }

  return {
    message,
    ...errorDetails,
  };
};

/**
 * Handles API requests with standardized error handling and optional callbacks
 * @param {Object} options - The request options
 * @param {Function} options.makeRequest - Function that makes the API request
 * @param {string|null} [options.overrideErrorMessage] - Optional custom error message
 * @param {Function|null} [options.successCallback] - Optional callback to execute on success
 * @param {Function|null} [options.errorCallback] - Optional callback to execute on error
 * @param {Function|null} [options.finallyCallback] - Optional callback to execute in finally block
 * @param {boolean} [options.hideErrorToast=false] - Whether to hide error toast notifications
 * @param {number[]} [options.expectedStatusCodes=[]] - HTTP status codes to treat as expected errors (skips Sentry)
 * @returns {Promise<any>} The result of the API request or undefined on error
 */
export const handleApiRequest = async ({
  makeRequest,
  overrideErrorMessage,
  successCallback,
  errorCallback,
  finallyCallback,
  hideErrorToast = false,
  expectedStatusCodes = [],
}) => {
  try {
    const result = await makeRequest();
    if (successCallback) await successCallback(result);
    return result;
  } catch (error) {
    const formattedError = formatApiError(error, overrideErrorMessage);

    if (expectedStatusCodes.includes(formattedError.status)) {
      formattedError.type = ERROR_TYPES.EXPECTED_ERROR;
    }

    //Log and toast (Sentry skip logic is in this function)
    logFrontendError(formattedError.message, formattedError, !hideErrorToast ? formattedError.message : undefined);

    // Call errorCallback if provided
    if (errorCallback) await errorCallback(error, formattedError);
  } finally {
    if (finallyCallback) await finallyCallback();
  }

  return;
};

export function getSubdomain(companyName) {
  const subdomain = slugify(companyName).slice(0, 55);
  const randomString = Math.random().toString(36).substring(2, 6);

  return `${subdomain}-${randomString}`;
}

export function getBaseUrl(subdomain, customDomain) {
  if (!subdomain) {
    return "";
  }

  if (customDomain) {
    return `https://${customDomain}`;
  }

  if (process.env.NODE_ENV === "development") {
    return `http://${subdomain}.${process.env.NEXT_PUBLIC_PLATFORMS}`;
  }

  return `https://${subdomain}.${process.env.NEXT_PUBLIC_PLATFORMS}`;
}

export const getUrlPath = (slug, pageType) => {
  if (pageType === PageType.SERVICE) {
    return `/services/${slug}`;
  } else if (pageType === PageType.PROJECT) {
    return `/projects/${slug}`;
  } else if (pageType === PageType.SERVICE_AREA) {
    return `/service-areas/${slug}`;
  } else if (pageType === PageType.BLOG) {
    return `/blog/${slug}`;
  } else if (pageType === PageType.PPC) {
    return `/ppc/${slug}`;
  } else {
    return `/${slug}`;
  }
};

export const validationRules = {
  name: {
    required: "Name is required",
    minLength: {
      value: 2,
      message: "Name must be at least 2 characters",
    },
  },
  email: {
    required: "Email is required",
    pattern: {
      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
      message: "Invalid email address",
    },
  },
  phone: {
    pattern: {
      value: /^(\(\d{3}\)\s\d{3}-\d{4}|\+?\d{10,14})$/,
      message: "Invalid phone number",
    },
  },
  message: {
    minLength: {
      value: 4,
      message: "Message must be at least 4 characters",
    },
  },
  postal_code: {
    pattern: {
      value: /^\d{5}(?:[-\s]\d{4})?$/,
      message: "Invalid postal code",
    },
  },
};

export function formatISODate(
  isoDateString,
  locale = "en-US",
  options = { year: "numeric", month: "long", day: "numeric" }
) {
  // Convert the ISO string to a Date object
  const dateObject = new Date(isoDateString);

  // Format the date to a human-readable string
  const formattedDate = new Intl.DateTimeFormat(locale, options).format(dateObject);

  return formattedDate;
}

export const validateField = (fieldName, fieldValue) => {
  const rules = validationRules[fieldName];
  if (!rules) return true;

  if (rules.required && !fieldValue) {
    return rules.required;
  }

  if (rules.minLength && fieldValue.length < rules.minLength.value) {
    return rules.minLength.message;
  }

  if (rules.pattern && !rules.pattern.value.test(fieldValue)) {
    return rules.pattern.message;
  }

  return true;
};

export const validateFieldFormat = (fieldName, fieldValue) => {
  const rules = validationRules[fieldName];
  if (!rules) return true;

  if (!fieldValue) return true;

  if (rules.minLength && fieldValue.length < rules.minLength.value) {
    return rules.minLength.message;
  }

  if (rules.pattern && !rules.pattern.value.test(fieldValue)) {
    return rules.pattern.message;
  }

  return true;
};

export const stripHTMLTags = (html) => {
  if (html) {
    html = html.replace(/<img[^>]*>/g, "");
    html = html.replace(/<\/?[^>]+(>|$)/g, "");
  }

  return html;
};

export const formatReviewerName = (name) => {
  if (!name) return "Anonymous";
  const [firstName, ...rest] = name.trim().split(" ");
  const lastInitial = rest.length > 0 ? `${rest.pop()[0]}.` : "";
  return `${firstName.charAt(0).toUpperCase()}${firstName.slice(1).toLowerCase()} ${lastInitial}`.trim();
};

export function formatDomainToWww(domain) {
  domain = domain.trim();

  const domainRegex = /^(?:https?:\/\/)?(?:www\.)?(.*)$/;
  const match = domain.match(domainRegex);
  const cleanDomain = match ? match[1] : domain;

  return `www.${cleanDomain}`;
}

// Format a phone number to a standard (xxx) xxx-xxxx format
export function formatPhoneNumber(phoneNumber = "") {
  if (phoneNumber.startsWith("+1")) {
    phoneNumber = phoneNumber.slice(2);
  }

  // Ensure the input is a string to use string methods
  const cleanNumber = phoneNumber.toString();

  // Use a RegExp to match and rearrange the parts of the number
  const match = cleanNumber.match(/^(\d{3})(\d{3})(\d{4})$/);

  // If the input is valid, reformat it. Otherwise, return an original or a placeholder value
  return match ? `(${match[1]}) ${match[2]}-${match[3]}` : "Invalid number";
}

export function whiteOrBlack(hex) {
  const color = tinycolor(hex);
  return tinycolor.mostReadable(color, ["#222428", "#fff"]).toHexString();
}

/**
 * Resolves a color reference to a hex color value.
 * Supports "primary", "secondary", and derivative keys like "color-700".
 *
 * @param {string | null | undefined} colorReference
 * @param {{
 *   primaryColor?: string | null,
 *   secondaryColor?: string | null,
 *   derivativeColors?: unknown | null
 * } | undefined} website
 *
 * @returns {string}
 */
export function resolveColorReference(colorReference, website) {
  const FALLBACK_BLACK_COLOR = "#000000";

  const primaryColor = website?.primaryColor;
  const secondaryColor = website?.secondaryColor;
  const derivativeColors = website?.derivativeColors;

  if (!colorReference) {
    return primaryColor || FALLBACK_BLACK_COLOR;
  }

  const normalizedColorReference = colorReference.trim().toLowerCase();

  // Handle special references
  if (normalizedColorReference === "primary" && primaryColor) {
    return primaryColor;
  }

  if (normalizedColorReference === "secondary" && secondaryColor) {
    return secondaryColor;
  }

  // Handle derivative color keys (e.g., "color-700", "color-500")
  if (
    normalizedColorReference.startsWith("color-") &&
    derivativeColors &&
    typeof derivativeColors === "object" &&
    !Array.isArray(derivativeColors)
  ) {
    const derivativeColor = derivativeColors[normalizedColorReference];
    if (typeof derivativeColor === "string" && derivativeColor) {
      return derivativeColor;
    }
  }

  // Fallback to primary color if reference is invalid
  return primaryColor || FALLBACK_BLACK_COLOR;
}

export function shuffleArray(array) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

export function websiteSuportsServiceAreas(website) {
  return ["SAB", "HYBRID", "PHYSICAL"].includes(website?.serviceAreaType); // now physical and hybrid work the SAME way
}

/**
 * Calculate differences between two arrays of objects based on a specific key in the objects.
 *
 * @param {Object[]} incoming - The array of new objects to compare.
 * @param {Object[]} outgoing - The array of previous objects to compare against.
 * @param {string} [key='id'] - The key used to identify the value to diff against in both arrays. Defaults to 'id'.
 * @returns {{valuesToConnect: Array<*>, valuesToDisconnect: Array<*>}} An object containing arrays of values to connect and disconnect.
 * @example
 *
 * const incoming = [{ id: 1 }, { id: 2 }, { id: 3 }];
 * const outgoing = [{ id: 2 }, { id: 3 }, { id: 4 }];
 *
 * const result = calculateArrayDiff(incoming, outgoing, 'id');
 * console.log(result); // { valuesToConnect: [{ id: 1 }], valuesToDisconnect: [{ id: 4 }] }
 */
export const calculateArrayDiff = (incoming = [], outgoing = [], key = "id") => {
  const newVals = new Set(incoming.map((item) => item[key]));
  const oldVals = new Set(outgoing.map((item) => item[key]));

  const valuesToConnect = [];
  const valuesToDisconnect = [];

  newVals.forEach((value) => {
    if (!oldVals.has(value)) {
      valuesToConnect.push({ [key]: value });
    }
  });

  oldVals.forEach((value) => {
    if (!newVals.has(value)) {
      valuesToDisconnect.push({ [key]: value });
    }
  });

  return {
    valuesToConnect,
    valuesToDisconnect,
  };
};

export const replaceItemAtIndex = (arr, newItem, index) => {
  let newArr = [...arr];

  newArr[index] = newItem;

  return newArr;
};

export function getEmailRecipients(website, organization) {
  const organizationEmails = [];

  for (const user of organization) {
    if (user?.publicUserData?.identifier) {
      organizationEmails.push(user.publicUserData.identifier.toLowerCase());
    }
  }

  const locationEmail = website?.locations?.[0]?.email?.toLowerCase();

  if (locationEmail && !organizationEmails.includes(locationEmail)) {
    organizationEmails.push(locationEmail);
  }

  return organizationEmails;
}

// Next.js complains about Date objects.
export function serializeForServerProps(obj) {
  if (obj === null || obj === undefined) return obj;

  // If obj is a Date, convert to ISO string.
  if (obj instanceof Date) return obj.toISOString();

  // If obj is an array, apply to each element.
  if (Array.isArray(obj)) {
    return obj.map(serializeForServerProps);
  }

  // If obj is an object, apply to each value.
  if (typeof obj === "object") {
    return Object.keys(obj).reduce((acc, key) => {
      acc[key] = serializeForServerProps(obj[key]);
      return acc;
    }, {});
  }

  return obj;
}

export function prodLog(...args) {
  if (process.env.NODE_ENV === "production") {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
}

export function devLog(...args) {
  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
}

export function navigateAndPreserveParams(toRoute, router, additionalParams = {}) {
  router.push({
    pathname: toRoute,
    query: {
      ...router.query,
      ...additionalParams,
    },
  });
}

const IMAGE_KIT = "imagekit";
const BASE_IMAGEKIT_URL = process.env.NEXT_PUBLIC_IMAGE_KIT_ENDPOINT;

export function constructImageUrl(providerId, source = IMAGE_KIT, { transform = true } = {}) {
  const BASE_TRANSFORM = "/tr:w-1000,f-auto,lo-true,q-80";
  switch (source) {
    case IMAGE_KIT:
      return `${BASE_IMAGEKIT_URL}${transform ? BASE_TRANSFORM : ""}/${providerId}`;
    default:
      return providerId;
  }
}

export function getSectionBgImageObject(url, { source = "imagekit", alt = "" } = {}) {
  if (url != null && typeof url === "object" && typeof url.url === "string") {
    url = url.url;
  }

  // Ensure alt is a string
  const safeAlt = typeof alt === "string" ? alt : "";

  return {
    url,
    providerId: extractImageId(url),
    source,
    alt: safeAlt,
  };
}

// This will basically extract the last part of the url including the file extension
// e.g. https://ik.imagekit.io/1234/image_abcd.jpg -> image_abcd.jpg (providerId)
// Normally we get the imagekit url, but when we store the image in the database, we store the providerId
// so we need to extract the id from the url
export function extractImageId(url) {
  const regex = /\/([^\/]+)$/;
  const match = url?.match(regex);
  return match ? match[1] : null;
}

export function getPhotoObject(url, { source = "imagekit", websiteId } = { websiteId: "" }) {
  return {
    providerId: extractImageId(url),
    source,
    websiteId,
  };
}

export function getScheduleTypeText(scheduleType, { dayOfWeek, weekOfMonth, daysOfWeekToPost }) {
  if (scheduleType === ScheduleType.MONTHLY) {
    return `${capitalize(weekOfMonth)} ${capitalize(dayOfWeek)} of the month`;
  } else if (scheduleType === ScheduleType.MULTIPLE_TIMES_PER_WEEK) {
    return `${daysOfWeekToPost.map((day) => capitalize(day)).join(", ")}`;
  }
}

export async function generateQRCodeBuffer(url) {
  try {
    const base64Image = await QRCode.toDataURL(url);
    const base64String = base64Image.split(",")[1];
    const imageBuffer = Buffer.from(base64String, "base64");
    return imageBuffer;
  } catch (error) {
    throw new ExternalError("Error generating QR Code", error);
  }
}

export function sanitizeContent(obj) {
  if (typeof obj === "string") {
    // Remove null characters and other problematic Unicode
    return obj.replace(/\u0000/g, "").replace(/[\u0000-\u0008\u000B-\u000C\u000E-\u001F]/g, "");
  }
  if (typeof obj === "object" && obj !== null) {
    if (Array.isArray(obj)) {
      return obj.map((item) => sanitizeContent(item));
    }
    const sanitized = {};
    for (const [key, value] of Object.entries(obj)) {
      sanitized[key] = sanitizeContent(value);
    }
    return sanitized;
  }
  return obj;
}

export const getCleanBaseUrl = (subdomain, customDomain) => {
  const cleanDomain = getBaseUrl(subdomain, customDomain).replace(/^(https?:\/\/)?(www\.)?/, "");
  return cleanDomain;
};

export const getIframeSrc = (options = {}) => {
  const { showControls = true, page, versionId, ...rest } = options;

  const protocol = isProd() ? "https" : "http";

  const baseUrl = `${protocol}://${page?.website?.subdomain}.${
    process.env.NEXT_PUBLIC_PLATFORMS
  }/${getUrlPath(page?.slug, page?.type)}`;

  const params = new URLSearchParams();

  params.set(showControls ? "editor" : "preview", "true");

  if (versionId) params.set("version_id", versionId);

  Object.entries(rest).forEach(([key, value]) => {
    if (value === undefined || value === null || value === false) return;
    params.set(key, String(value));
  });

  const queryString = params.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

/**
 * Generate random alphanumeric string for subdomain
 * @param {number} length - The length of the string to generate
 * @returns {string} - Random alphanumeric string
 */
export function generateRandomString(length) {
  const characters = "abcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

/** @deprecated use `delay` from `@/lib/utils/time` instead */
export function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// this function is also duplicated on platforms
export function getRoundednessValue(roundedness) {
  switch (roundedness) {
    case RoundednessLevel.NONE:
      return "0px";
    case RoundednessLevel.FULL:
      return "28px";
    case RoundednessLevel.MEDIUM:
    default:
      return "8px";
  }
}

/**
 * Formats a number into a compact string representation.
 * Examples:
 *   1500 → "1.5K"
 *   -1500 → "-1.5K"
 *   2000000 → "2.0M"
 *   -2000000 → "-2.0M"
 */
export function formatCompactCount(value) {
  if (value == null || Number.isNaN(value)) return "—";

  return new Intl.NumberFormat("en", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}
